import { lazy, Suspense, useRef, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { SettingsValuesProvider } from './contexts/SettingsValuesContext';
import { UnsavedChangesProvider } from './contexts/UnsavedChangesContext';
import { StylingProvider } from './contexts/StylingContext';
import { Toaster } from './components/ui/sonner';
import { ErrorBoundary } from './components/shared/ErrorBoundary';
import VersionChecker from './components/shared/VersionChecker';
import { RouteFallback } from './components/shared/RouteFallback';

// Layout
import MainLayout from './components/layout/MainLayout';
const MockupTimesheetAll = lazy(() => import('./pages/_MockupTimesheetAll'));

// Permission components
import { PermissionRoute, AccessDenied } from './components/shared/PermissionGate';

// Pages — every one of them fetched on demand (hardening P4 step 1).
//
// These used to be 154 ordinary imports, which meant every visitor downloaded the
// entire application — billing, the workflow builder, the client portal, all of it —
// before being shown a login box. Each page is now its own file, fetched the first
// time somebody asks for that route and cached from then on. The waiting is covered by
// <Suspense>: inside a layout only the content area waits, so the sidebar and header
// stay put.
//
// LoginPage and PortalLoginPage stay eager on purpose. They are the first thing a
// visitor sees, before any session exists, and a spinner on the very first paint is the
// worst place to have one. `scripts/check-route-splitting.js` enforces exactly that
// list, because one ordinary import here would silently put a whole page tree back into
// the first download.
import LoginPage from './pages/auth/LoginPage';
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const ClientsPage = lazy(() => import('./pages/clients/ClientsPage'));
const ClientFormPage = lazy(() => import('./pages/clients/ClientFormPage'));
const ClientDetailPage = lazy(() => import('./pages/clients/ClientDetailPage'));
const SiteDetailPage = lazy(() => import('./pages/clients/SiteDetailPage'));
const AssetsPage = lazy(() => import('./pages/assets/AssetsPage'));
const AssetFormPage = lazy(() => import('./pages/assets/AssetFormPage'));
const AssetDetailPage = lazy(() => import('./pages/assets/AssetDetailPage'));
const TicketsPage = lazy(() => import('./pages/tickets/TicketsPage'));
const GlobalSearchPage = lazy(() => import('./pages/search/GlobalSearchPage'));
const TicketFormPage = lazy(() => import('./pages/tickets/TicketFormPage'));
const TicketDetailPage = lazy(() => import('./pages/tickets/TicketDetailPage'));
const TimePage = lazy(() => import('./pages/time/TimePage'));
const TimeEntryFormPage = lazy(() => import('./pages/time/TimeEntryFormPage'));
const InvoiceFormPage = lazy(() => import('./pages/billing/InvoiceFormPage'));
const InvoiceEditPage = lazy(() => import('./pages/billing/InvoiceEditPage'));
const ContractFormPage = lazy(() => import('./pages/billing/ContractFormPage'));
const ValuesPage = lazy(() => import('./pages/settings/ValuesPage'));
const ProjectTasksSettingsPage = lazy(() => import('./pages/settings/ProjectTasksSettingsPage'));
const UsersPage = lazy(() => import('./pages/settings/UsersPage'));
const SettingsHubPage = lazy(() => import('./pages/settings/SettingsHubPage'));
const SystemSettingsPage = lazy(() => import('./pages/settings/SystemSettingsPage'));
const ServiceAliasesPage = lazy(() => import('./pages/settings/ServiceAliasesPage'));
const AssetConfigPage = lazy(() => import('./pages/settings/AssetConfigPage'));
const EmailSettingsPage = lazy(() => import('./pages/settings/EmailSettingsPage'));
const EmailReviewPage = lazy(() => import('./pages/EmailReviewPage'));
const CompanySettingsPage = lazy(() => import('./pages/settings/CompanySettingsPage'));
const AppStylingPage = lazy(() => import('./pages/settings/AppStylingPage'));
const AISettingsPage = lazy(() => import('./pages/settings/AISettingsPage'));
const ChangelogPage = lazy(() => import('./pages/settings/ChangelogPage'));
const CompletionRulesPage = lazy(() => import('./pages/settings/CompletionRulesPage'));
const NotificationSettingsPage = lazy(() => import('./pages/settings/NotificationSettingsPage'));
const MyNotificationsPage = lazy(() => import('./pages/settings/MyNotificationsPage'));
const AlertSettingsPage = lazy(() => import('./pages/settings/AlertSettingsPage'));
const ProjectsPage = lazy(() => import('./pages/projects/ProjectsPage'));
const MyTasksPage = lazy(() => import('./pages/tasks/MyTasksPage'));
const ProjectFormPage = lazy(() => import('./pages/projects/ProjectFormPage'));
const ProjectDetailPage = lazy(() => import('./pages/projects/ProjectDetailPage'));
const BillingPage = lazy(() => import('./pages/billing/BillingPage'));
const ServiceRenewalsPage = lazy(() => import('./pages/billing/ServiceRenewalsPage'));
const RetainerBurndownPage = lazy(() => import('./pages/billing/RetainerBurndownPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const ScheduledTasksPage = lazy(() => import('./pages/scheduler/ScheduledTasksPage'));
const RoadmapPage = lazy(() => import('./pages/roadmap/RoadmapPage'));
const AuditLogsPage = lazy(() => import('./pages/logs/AuditLogsPage'));
const EmailLogsPage = lazy(() => import('./pages/logs/EmailLogsPage'));
const BackgroundTaskLogsPage = lazy(() => import('./pages/logs/BackgroundTaskLogsPage'));
const LogSettingsPage = lazy(() => import('./pages/logs/LogSettingsPage'));
const SyncLogsPage = lazy(() => import('./pages/logs/SyncLogsPage'));
const SecurityEventsPage = lazy(() => import('./pages/logs/SecurityEventsPage'));
const TicketPrintPage = lazy(() => import('./pages/tickets/TicketPrintPage'));
const ProjectPrintPage = lazy(() => import('./pages/projects/ProjectPrintPage'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const TaxRatesPage = lazy(() => import('./pages/billing/TaxRatesPage'));
const RateTimelinePage = lazy(() => import('./pages/billing/RateTimelinePage'));
const ServicesPage = lazy(() => import('./pages/settings/ServicesPage'));
const ContractDetailPage = lazy(() => import('./pages/billing/ContractDetailPage'));
const ContractsReportPage = lazy(() => import('./pages/billing/ContractsReportPage'));
const BillingReviewPage = lazy(() => import('./pages/billing/BillingReviewPage'));
const CourierBillingPage = lazy(() => import('./pages/billing/CourierBillingPage'));
const StockPage = lazy(() => import('./pages/stock/StockPage'));
const StockDetailPage = lazy(() => import('./pages/stock/StockDetailPage'));
const StorageLayoutPage = lazy(() => import('./pages/stock/StorageLayoutPage'));
const ContactsPage = lazy(() => import('./pages/contacts/ContactsPage'));
const ContactDetailPage = lazy(() => import('./pages/contacts/ContactDetailPage'));
const ContactEditRedirect = lazy(() => import('./pages/contacts/ContactEditRedirect'));
const TeamsPage = lazy(() => import('./pages/settings/TeamsPage'));
const RolesSettingsPage = lazy(() => import('./pages/settings/RolesSettingsPage'));
const SchedulerPage = lazy(() => import('./pages/scheduler/SchedulerPage'));
const SiteVisitsQueuePage = lazy(() => import('./pages/SiteVisitsQueuePage'));
const PendingTimePage = lazy(() => import('./pages/billing/PendingTimePage'));
const PendingItemsPage = lazy(() => import('./pages/billing/PendingItemsPage'));
const BillingDiagnosticsPage = lazy(() => import('./pages/billing/BillingDiagnosticsPage'));
const ZeroPricedPage = lazy(() => import('./pages/billing/ZeroPricedPage'));
const MissingLinesPage = lazy(() => import('./pages/billing/MissingLinesPage'));
const ServiceSummariesPage = lazy(() => import('./pages/billing/ServiceSummariesPage'));
const ServiceSummaryPrintPage = lazy(() => import('./pages/billing/ServiceSummaryPrintPage'));
const BillingEntityDashboard = lazy(() => import('./pages/billing/BillingEntityDashboard'));
const ClientStatementsPage = lazy(() => import('./pages/billing/ClientStatementsPage'));
const DryRunPage = lazy(() => import('./pages/billing/DryRunPage'));
const IncludedValuePage = lazy(() => import('./pages/billing/IncludedValuePage'));
const EntityStatementPage = lazy(() => import('./pages/billing/EntityStatementPage'));
const EntityStatementPrintPage = lazy(() => import('./pages/billing/EntityStatementPrintPage'));
const TimeReportPage = lazy(() => import('./pages/time/TimeReportPage'));
const NinjaOneSettingsPage = lazy(() => import('./pages/settings/NinjaOneSettingsPage'));
const M365SettingsPage = lazy(() => import('./pages/settings/M365SettingsPage'));
const IntegrationHealthPage = lazy(() => import('./pages/integrations/IntegrationHealthPage'));
const AssetTopologyPage = lazy(() => import('./pages/assets/AssetTopologyPage'));
const ServicesOverviewPage = lazy(() => import('./pages/assets/ServicesOverviewPage'));
const KBListPage = lazy(() => import('./pages/kb/KBListPage'));
const KBEditorPage = lazy(() => import('./pages/kb/KBEditorPage'));
const KBViewPage = lazy(() => import('./pages/kb/KBViewPage'));
const KBPrintPage = lazy(() => import('./pages/kb/KBPrintPage'));
const WorkflowBuilderPage = lazy(() => import('./pages/kb/WorkflowBuilderPage'));
const WorkflowsListPage = lazy(() => import('./pages/kb/WorkflowsListPage'));
const BuildValueKeysPage = lazy(() => import('./pages/kb/BuildValueKeysPage'));
const WorkflowImportPage = lazy(() => import('./pages/kb/WorkflowImportPage'));
const OrdersPage = lazy(() => import('./pages/orders/OrdersPage'));
const OrderEditPage = lazy(() => import('./pages/orders/OrderEditPage'));
const OrderRequestsPage = lazy(() => import('./pages/orders/OrderRequestsPage'));
const GoodsInPage = lazy(() => import('./pages/stock/GoodsInPage'));
const StorageLocationsPage = lazy(() => import('./pages/settings/StorageLocationsPage'));
const StorageUsagePage = lazy(() => import('./pages/settings/StorageUsagePage'));
const OneOffMigrationsPage = lazy(() => import('./pages/settings/OneOffMigrationsPage'));
const ServiceReviewPage = lazy(() => import('./pages/settings/ServiceReviewPage'));
const InternalTimesheetPage = lazy(() => import('./pages/time/InternalTimesheetPage'));
const InternalCategoriesPage = lazy(() => import('./pages/settings/InternalCategoriesPage'));
const XeroSettingsPage = lazy(() => import('./pages/settings/XeroSettingsPage'));
const TimerCleanupPage = lazy(() => import('./pages/settings/TimerCleanupPage'));
const HealthChecksPage = lazy(() => import('./pages/settings/HealthChecksPage'));
const SLASettingsPage = lazy(() => import('./pages/settings/SLASettingsPage'));
const ClientFieldsPage = lazy(() => import('./pages/settings/ClientFieldsPage'));
const WorkflowsPage = lazy(() => import('./pages/settings/WorkflowsPage'));
const NoteTemplatesPage = lazy(() => import('./pages/settings/NoteTemplatesPage'));
const SettingsWorkflowBuilderPage = lazy(() => import('./pages/settings/WorkflowBuilderPage'));
const WorkflowLogPage = lazy(() => import('./pages/settings/WorkflowLogPage'));
const EmailThreadingMockupPage = lazy(() => import('./pages/mockups/EmailThreadingMockupPage'));
const ServiceSummaryMockupPage = lazy(() => import('./pages/mockups/ServiceSummaryMockupPage'));
const ServiceSummaryByPersonMockupPage = lazy(() => import('./pages/mockups/ServiceSummaryByPersonMockupPage'));
const ResolveDialogMockupPage = lazy(() => import('./pages/mockups/ResolveDialogMockupPage'));
const ReportsPage = lazy(() => import('./pages/reports/ReportsPage'));
const TimeReportsPage = lazy(() => import('./pages/reports/TimeReportsPage'));
const SLAReportsPage = lazy(() => import('./pages/reports/SLAReportsPage'));
const TicketVolumeReportsPage = lazy(() => import('./pages/reports/TicketVolumeReportsPage'));
const BacklogReportsPage = lazy(() => import('./pages/reports/BacklogReportsPage'));
const ClientProfitabilityPage = lazy(() => import('./pages/reports/ClientProfitabilityPage'));
const EngineerPerformancePage = lazy(() => import('./pages/reports/EngineerPerformancePage'));
const AssetReportsPage = lazy(() => import('./pages/reports/AssetReportsPage'));
const RenewalCleanupPrintPage = lazy(() => import('./pages/reports/RenewalCleanupPrintPage'));
const ContactCoverageReportsPage = lazy(() => import('./pages/reports/ContactCoverageReportsPage'));
const ByodTemplatesPage = lazy(() => import('./pages/settings/ByodTemplatesPage'));
const ByodComplianceReportPage = lazy(() => import('./pages/reports/ByodComplianceReportPage'));
const ClientSetupReportPage = lazy(() => import('./pages/reports/ClientSetupReportPage'));
const SecretRevealsReportPage = lazy(() => import('./pages/reports/SecretRevealsReportPage'));
const WorkflowQcReportPage = lazy(() => import('./pages/reports/WorkflowQcReportPage'));
const ClientEstateReportPage = lazy(() => import('./pages/reports/ClientEstateReportPage'));

// Portal Pages
import PortalLoginPage from './pages/portal/PortalLoginPage';
const PortalSetupPage = lazy(() => import('./pages/portal/PortalSetupPage'));
const PortalForgotPasswordPage = lazy(() => import('./pages/portal/PortalForgotPasswordPage'));
const PortalDashboard = lazy(() => import('./pages/portal/PortalDashboard'));
const PortalTicketList = lazy(() => import('./pages/portal/PortalTicketList'));
const PortalTicketDetail = lazy(() => import('./pages/portal/PortalTicketDetail'));
const PortalTicketNew = lazy(() => import('./pages/portal/PortalTicketNew'));
const PortalMagicTicketPage = lazy(() => import('./pages/portal/PortalMagicTicketPage'));
const PortalAssetList = lazy(() => import('./pages/portal/PortalAssetList'));
const PortalInvoiceList = lazy(() => import('./pages/portal/PortalInvoiceList'));
const PortalInvoiceDetail = lazy(() => import('./pages/portal/PortalInvoiceDetail'));
const PortalContractList = lazy(() => import('./pages/portal/PortalContractList'));
const PortalContractDetail = lazy(() => import('./pages/portal/PortalContractDetail'));
const PortalProjectList = lazy(() => import('./pages/portal/PortalProjectList'));
const PortalProjectDetail = lazy(() => import('./pages/portal/PortalProjectDetail'));
const PortalKBList = lazy(() => import('./pages/portal/PortalKBList'));
const PortalKBArticle = lazy(() => import('./pages/portal/PortalKBArticle'));
const PortalProfilePage = lazy(() => import('./pages/portal/PortalProfilePage'));
const PortalUsersPage = lazy(() => import('./pages/portal/PortalUsersPage'));
const PortalResetPasswordPage = lazy(() => import('./pages/portal/PortalResetPasswordPage'));
const PortalNotFoundPage = lazy(() => import('./pages/portal/PortalNotFoundPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
import PortalLayout from './components/portal/PortalLayout';
const PortalMagicLinksPage = lazy(() => import('./pages/settings/PortalMagicLinksPage'));
const PortalUsersOverviewPage = lazy(() => import('./pages/settings/PortalUsersOverviewPage'));
const PortalUsageReportPage = lazy(() => import('./pages/reports/PortalUsageReportPage'));
const NonBillableReportPage = lazy(() => import('./pages/reports/NonBillableReportPage'));
const KBConnectivityReportPage = lazy(() => import('./pages/reports/KBConnectivityReportPage'));
const RevenueByContractPage = lazy(() => import('./pages/reports/RevenueByContractPage'));
const ProjectQuotedVsActualPage = lazy(() => import('./pages/reports/ProjectQuotedVsActualPage'));
import { PortalAuthProvider, usePortalAuth } from './contexts/PortalAuthContext';

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    // Remember the page the user was on so we can return them here post-login.
    const path = location.pathname + location.search;
    if (path && !path.startsWith('/login') && !path.startsWith('/portal')) {
      try { localStorage.setItem('returnTo', path); } catch (_) { /* ignore */ }
    }
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Public Route wrapper (redirects to dashboard if already logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  // Capture the post-login destination ONCE. We must not mutate localStorage
  // during render (StrictMode double-invokes render and would clear it early,
  // dropping us to '/'). Read-only here; the actual clear happens in an effect.
  const destRef = useRef(null);
  if (destRef.current === null) {
    let dest = '/';
    try {
      const rt = localStorage.getItem('returnTo');
      if (rt && rt.startsWith('/') && !rt.startsWith('/login') && !rt.startsWith('/portal')) {
        dest = rt;
      }
    } catch (_) { /* ignore */ }
    destRef.current = dest;
  }
  useEffect(() => {
    if (isAuthenticated) {
      try { localStorage.removeItem('returnTo'); } catch (_) { /* ignore */ }
    }
  }, [isAuthenticated]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to={destRef.current} replace />;
  }
  
  return children;
};

// Portal Protected Route wrapper
const PortalProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = usePortalAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/portal/login" replace />;
  return children;
};

const PortalPublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = usePortalAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }
  if (isAuthenticated) return <Navigate to="/portal" replace />;
  return children;
};

function AppRoutes() {
  return (
    // The outer boundary covers the routes that render with no chrome at all — the
    // print views and the portal's public pages. Inside a layout the boundary around
    // <Outlet /> catches first, so navigation keeps the sidebar and header on screen.
    <Suspense fallback={<RouteFallback fullScreen />}>
    <Routes>
      {/* Public routes */}
      {/* TEMP mockup route (design approval) — remove after sign-off */}
      <Route path="/mockup/timesheet-all" element={<MockupTimesheetAll />} />
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />

      {/* Print route - standalone, no layout */}
      <Route
        path="/tickets/:id/print"
        element={
          <ProtectedRoute>
            <TicketPrintPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing/service-summaries/:id/print"
        element={
          <ProtectedRoute>
            <ServiceSummaryPrintPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing/entities/:entityId/statement/print"
        element={
          <ProtectedRoute>
            <EntityStatementPrintPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/assets/renewal-cleanup/print"
        element={
          <ProtectedRoute>
            <RenewalCleanupPrintPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/kb/:id/print"
        element={
          <ProtectedRoute>
            <KBPrintPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:id/print"
        element={
          <ProtectedRoute>
            <ProjectPrintPage />
          </ProtectedRoute>
        }
      />
      
      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="search" element={<GlobalSearchPage />} />

        
        {/* Email Review (Triage) — open to anyone with the triageEmails permission */}
        <Route path="email-review" element={
          <PermissionRoute resource="admin" action="triageEmails" showAccessDenied>
            <EmailReviewPage />
          </PermissionRoute>
        } />
        
        {/* Clients */}
        <Route path="clients" element={<ClientsPage />} />
        <Route path="clients/new" element={
          <PermissionRoute resource="clients" action="create" showAccessDenied>
            <ClientFormPage />
          </PermissionRoute>
        } />
        <Route path="clients/:id" element={<ClientDetailPage />} />
        <Route path="clients/:id/edit" element={
          <PermissionRoute resource="clients" action="edit" showAccessDenied>
            <ClientFormPage />
          </PermissionRoute>
        } />
        <Route path="clients/:clientId/topology" element={<AssetTopologyPage />} />
        <Route path="clients/:id/sites/:siteId" element={<SiteDetailPage />} />
        
        {/* Contacts */}
        <Route path="contacts" element={<ContactsPage />} />
        <Route path="contacts/:clientId/:contactId" element={<ContactDetailPage />} />
        <Route path="contacts/:clientId/:contactId/edit" element={<ContactEditRedirect />} />
        
        {/* Assets */}
        <Route path="assets" element={<AssetsPage />} />
        <Route path="assets/services-overview" element={<ServicesOverviewPage />} />
        <Route path="assets/new" element={
          <PermissionRoute resource="assets" action="create" showAccessDenied>
            <AssetFormPage />
          </PermissionRoute>
        } />
        <Route path="assets/:id" element={<AssetDetailPage />} />
        
        {/* Tickets */}
        <Route path="tickets" element={<TicketsPage />} />
        <Route path="tickets/new" element={
          <PermissionRoute resource="tickets" action="create" showAccessDenied>
            <TicketFormPage />
          </PermissionRoute>
        } />
        <Route path="tickets/:id" element={<TicketDetailPage />} />
        
        {/* Scheduler */}
        <Route path="scheduler" element={
          <PermissionRoute resource="scheduling" action="view" showAccessDenied>
            <SchedulerPage />
          </PermissionRoute>
        } />
        <Route path="site-visits" element={
          <PermissionRoute resource="scheduling" action="view" showAccessDenied>
            <SiteVisitsQueuePage />
          </PermissionRoute>
        } />
        
        {/* Time */}
        <Route path="time" element={<TimePage />} />
        <Route path="time/new" element={<TimeEntryFormPage />} />
        
        {/* Projects */}
        <Route path="projects" element={<ProjectsPage />} />
        <Route path="projects/new" element={
          <PermissionRoute resource="projects" action="create" showAccessDenied>
            <ProjectFormPage />
          </PermissionRoute>
        } />
        <Route path="projects/:id" element={<ProjectDetailPage />} />
        <Route path="my-tasks" element={<MyTasksPage />} />
        
        {/* Billing - Protected by billing.view permission */}
        <Route path="billing" element={
          <PermissionRoute resource="billing" action="view" showAccessDenied>
            <BillingPage />
          </PermissionRoute>
        } />
        <Route path="billing/review" element={
          <PermissionRoute resource="billing" action="view" showAccessDenied>
            <BillingReviewPage />
          </PermissionRoute>
        } />
        <Route path="billing/renewals" element={
          <PermissionRoute resource="billing" action="view" showAccessDenied>
            <ServiceRenewalsPage />
          </PermissionRoute>
        } />
        <Route path="billing/retainers" element={
          <PermissionRoute resource="billing" action="view" showAccessDenied>
            <RetainerBurndownPage />
          </PermissionRoute>
        } />
        <Route path="billing/courier" element={
          <PermissionRoute resource="billing" action="view" showAccessDenied>
            <CourierBillingPage />
          </PermissionRoute>
        } />
        <Route path="billing/pending-time" element={
          <PermissionRoute resource="billing" action="view" showAccessDenied>
            <PendingTimePage />
          </PermissionRoute>
        } />
        <Route path="billing/pending-items" element={
          <PermissionRoute resource="billing" action="view" showAccessDenied>
            <PendingItemsPage />
          </PermissionRoute>
        } />
        <Route path="billing/diagnostics" element={
          <PermissionRoute resource="billing" action="view" showAccessDenied>
            <BillingDiagnosticsPage />
          </PermissionRoute>
        } />
        <Route path="billing/zero-priced" element={
          <PermissionRoute resource="billing" action="view" showAccessDenied>
            <ZeroPricedPage />
          </PermissionRoute>
        } />
        <Route path="billing/rate-history" element={
          <PermissionRoute resource="billing" action="view" showAccessDenied>
            <RateTimelinePage />
          </PermissionRoute>
        } />
        <Route path="billing/missing-lines" element={
          <PermissionRoute resource="billing" action="view" showAccessDenied>
            <MissingLinesPage />
          </PermissionRoute>
        } />
        <Route path="billing/service-summaries" element={
          <PermissionRoute resource="billing" action="view" showAccessDenied>
            <ServiceSummariesPage />
          </PermissionRoute>
        } />
        <Route path="billing/time-report" element={
          <PermissionRoute resource="billing" action="view" showAccessDenied>
            <TimeReportPage />
          </PermissionRoute>
        } />
        <Route path="billing/entities" element={
          <PermissionRoute resource="billing" action="view" showAccessDenied>
            <BillingEntityDashboard />
          </PermissionRoute>
        } />
        <Route path="billing/statements" element={
          <PermissionRoute resource="billing" action="view" showAccessDenied>
            <ClientStatementsPage />
          </PermissionRoute>
        } />
        <Route path="billing/dry-run" element={
          <PermissionRoute resource="billing" action="view" showAccessDenied>
            <DryRunPage />
          </PermissionRoute>
        } />
        <Route path="billing/included-value" element={
          <PermissionRoute resource="billing" action="view" showAccessDenied>
            <IncludedValuePage />
          </PermissionRoute>
        } />
        <Route path="billing/entities/:entityId/statement" element={
          <PermissionRoute resource="billing" action="view" showAccessDenied>
            <EntityStatementPage />
          </PermissionRoute>
        } />
        <Route path="billing/invoices/new" element={
          <PermissionRoute resource="billing" action="create" showAccessDenied>
            <InvoiceFormPage />
          </PermissionRoute>
        } />
        <Route path="billing/invoices/:id/edit" element={
          <PermissionRoute resource="billing" action="edit" showAccessDenied>
            <InvoiceEditPage />
          </PermissionRoute>
        } />
        <Route path="billing/contracts/new" element={
          <PermissionRoute resource="billing" action="create" showAccessDenied>
            <ContractFormPage />
          </PermissionRoute>
        } />
        <Route path="billing/contracts-report" element={
          <PermissionRoute resource="billing" action="view" showAccessDenied>
            <ContractsReportPage />
          </PermissionRoute>
        } />
        <Route path="billing/contracts/:id" element={
          <PermissionRoute resource="billing" action="view" showAccessDenied>
            <ContractDetailPage />
          </PermissionRoute>
        } />
        <Route path="billing/contracts/:id/edit" element={
          <PermissionRoute resource="billing" action="edit" showAccessDenied>
            <ContractFormPage />
          </PermissionRoute>
        } />
        
        {/* Contract shortcut routes */}
        <Route path="contracts/:id" element={
          <PermissionRoute resource="billing" action="view" showAccessDenied>
            <ContractDetailPage />
          </PermissionRoute>
        } />
        
        {/* Services (billing permission required) */}
        <Route path="services" element={
          <PermissionRoute resource="billing" action="view" showAccessDenied>
            <ServicesPage />
          </PermissionRoute>
        } />
        
        {/* Stock / Inventory */}
        <Route path="stock" element={<StockPage />} />
        <Route path="stock/:id" element={<StockDetailPage />} />
        <Route path="storage-layouts" element={<StorageLayoutPage />} />

        {/* Integration Health Dashboard (admin only) */}
        <Route path="integrations" element={
          <PermissionRoute resource="admin" action="viewIntegrationHealth" showAccessDenied>
            <IntegrationHealthPage />
          </PermissionRoute>
        } />
        
        {/* Orders */}
        <Route path="orders" element={
          <PermissionRoute resource="billing" action="view" showAccessDenied>
            <OrdersPage />
          </PermissionRoute>
        } />
        <Route path="orders/requests" element={<OrderRequestsPage />} />
        <Route path="orders/:id" element={
          <PermissionRoute resource="billing" action="view" showAccessDenied>
            <OrderEditPage />
          </PermissionRoute>
        } />
        <Route path="orders/:id/edit" element={
          <PermissionRoute resource="billing" action="view" showAccessDenied>
            <OrderEditPage />
          </PermissionRoute>
        } />
        <Route path="goods-in" element={<GoodsInPage />} />
        
        {/* Scheduled Tasks */}
        <Route path="scheduled-tasks" element={<ScheduledTasksPage />} />
        <Route path="roadmap" element={<RoadmapPage />} />
        
        {/* Knowledge Base */}
        <Route path="kb" element={<KBListPage />} />
        <Route path="kb/workflows" element={<WorkflowsListPage />} />
        <Route path="kb/workflow-value-keys" element={<BuildValueKeysPage />} />
        <Route path="kb/build-value-keys" element={<BuildValueKeysPage />} />
        <Route path="kb/build-values" element={<BuildValueKeysPage />} />
        <Route path="kb/workflow-import" element={<WorkflowImportPage />} />
        <Route path="kb/new" element={<KBEditorPage />} />
        <Route path="kb/workflow-builder" element={<WorkflowBuilderPage />} />
        <Route path="kb/workflow-builder/:id" element={<WorkflowBuilderPage />} />
        <Route path="kb/:id" element={<KBViewPage />} />
        <Route path="kb/:id/edit" element={<KBEditorPage />} />
        
        {/* Reports */}
        <Route path="reports" element={
          <PermissionRoute
            resource="reports" action="view"
            showAccessDenied
          >
            <ReportsPage />
          </PermissionRoute>
        } />
        <Route path="reports/time" element={
          <PermissionRoute resource="reports" action="view" showAccessDenied>
            <TimeReportsPage />
          </PermissionRoute>
        } />
        <Route path="reports/sla" element={
          <PermissionRoute resource="reports" action="view" showAccessDenied>
            <SLAReportsPage />
          </PermissionRoute>
        } />
        <Route path="reports/tickets" element={
          <PermissionRoute resource="reports" action="view" showAccessDenied>
            <TicketVolumeReportsPage />
          </PermissionRoute>
        } />
        <Route path="reports/backlog" element={
          <PermissionRoute resource="reports" action="view" showAccessDenied>
            <BacklogReportsPage />
          </PermissionRoute>
        } />
        <Route path="reports/profitability" element={
          <PermissionRoute resource="reports" action="viewFinancial" showAccessDenied>
            <ClientProfitabilityPage />
          </PermissionRoute>
        } />
        <Route path="reports/engineers" element={
          <PermissionRoute resource="reports" action="viewFinancial" showAccessDenied>
            <EngineerPerformancePage />
          </PermissionRoute>
        } />
        <Route path="reports/assets" element={
          <PermissionRoute resource="reports" action="viewOperational" showAccessDenied>
            <AssetReportsPage />
          </PermissionRoute>
        } />
        <Route path="reports/contacts" element={
          <PermissionRoute resource="reports" action="viewOperational" showAccessDenied>
            <ContactCoverageReportsPage />
          </PermissionRoute>
        } />
        <Route path="reports/portal-usage" element={
          <PermissionRoute resource="admin" action="systemSettings" showAccessDenied>
            <PortalUsageReportPage />
          </PermissionRoute>
        } />
        <Route path="reports/byod" element={
          <PermissionRoute resource="reports" action="viewOperational" showAccessDenied>
            <ByodComplianceReportPage />
          </PermissionRoute>
        } />
        <Route path="reports/client-setup" element={
          <PermissionRoute resource="reports" action="view" showAccessDenied>
            <ClientSetupReportPage />
          </PermissionRoute>
        } />
        <Route path="reports/workflow-qc" element={
          <PermissionRoute resource="reports" action="view" showAccessDenied>
            <WorkflowQcReportPage />
          </PermissionRoute>
        } />
        <Route path="reports/secret-reveals" element={
          <PermissionRoute resource="admin" action="viewAuditLog" showAccessDenied>
            <SecretRevealsReportPage />
          </PermissionRoute>
        } />
        <Route path="reports/client-estate" element={
          <PermissionRoute resource="admin" action="systemSettings" showAccessDenied>
            <ClientEstateReportPage />
          </PermissionRoute>
        } />
        <Route path="reports/non-billable" element={
          <PermissionRoute resource="reports" action="view" showAccessDenied>
            <NonBillableReportPage />
          </PermissionRoute>
        } />
        <Route path="reports/kb-connectivity" element={
          <PermissionRoute resource="reports" action="view" showAccessDenied>
            <KBConnectivityReportPage />
          </PermissionRoute>
        } />
        <Route path="reports/revenue-by-contract" element={
          <PermissionRoute resource="reports" action="viewFinancial" showAccessDenied>
            <RevenueByContractPage />
          </PermissionRoute>
        } />
        <Route path="reports/project-quoted-vs-actual" element={
          <PermissionRoute resource="reports" action="viewFinancial" showAccessDenied>
            <ProjectQuotedVsActualPage />
          </PermissionRoute>
        } />
        <Route path="mockups/email-threading" element={<EmailThreadingMockupPage />} />
        <Route path="mockups/service-summary" element={<ServiceSummaryMockupPage />} />
        <Route path="mockups/service-summary-by-person" element={<ServiceSummaryByPersonMockupPage />} />
        <Route path="mockups/resolve-dialog" element={<ResolveDialogMockupPage />} />

        {/* Audit Logs - Protected by admin.viewAuditLog */}
        <Route path="audit" element={
          <PermissionRoute resource="admin" action="viewAuditLog" showAccessDenied>
            <AuditLogsPage />
          </PermissionRoute>
        } />
        
        {/* Email Logs - Protected by admin.viewAuditLog */}
        <Route path="email-logs" element={
          <PermissionRoute resource="admin" action="viewAuditLog" showAccessDenied>
            <EmailLogsPage />
          </PermissionRoute>
        } />
        
        {/* Security Events - Protected by admin.viewAuditLog */}
        <Route path="security-events" element={
          <PermissionRoute resource="admin" action="viewAuditLog" showAccessDenied>
            <SecurityEventsPage />
          </PermissionRoute>
        } />

        {/* Background Task Logs - Protected by admin.viewBackgroundTasks */}
        <Route path="background-task-logs" element={
          <PermissionRoute resource="admin" action="viewBackgroundTasks" showAccessDenied>
            <BackgroundTaskLogsPage />
          </PermissionRoute>
        } />

        {/* Log Settings - Protected by admin.systemSettings */}
        <Route path="log-settings" element={
          <PermissionRoute resource="admin" action="manageLogSettings" showAccessDenied>
            <LogSettingsPage />
          </PermissionRoute>
        } />

        {/* Sync Logs - Protected by admin.systemSettings */}
        <Route path="sync-logs" element={
          <PermissionRoute resource="admin" action="viewSyncLogs" showAccessDenied>
            <SyncLogsPage />
          </PermissionRoute>
        } />
        
        {/* Notifications */}
        <Route path="notifications" element={<NotificationsPage />} />
        
        {/* Settings Section - Protected routes */}
        <Route path="settings">
          <Route index element={<SettingsHubPage />} />
          <Route path="my-notifications" element={<MyNotificationsPage />} />
          <Route path="users" element={
            <PermissionRoute resource="admin" action="manageUsers" showAccessDenied>
              <UsersPage />
            </PermissionRoute>
          } />
          <Route path="teams" element={
            <PermissionRoute resource="admin" action="manageUsers" showAccessDenied>
              <TeamsPage />
            </PermissionRoute>
          } />
          <Route path="roles" element={
            <PermissionRoute resource="admin" action="manageUsers" showAccessDenied>
              <RolesSettingsPage />
            </PermissionRoute>
          } />
          <Route path="values" element={
            <PermissionRoute resource="admin" action="manageValues" showAccessDenied>
              <ValuesPage />
            </PermissionRoute>
          } />
          <Route path="project-tasks" element={
            <PermissionRoute resource="admin" action="manageValues" showAccessDenied>
              <ProjectTasksSettingsPage />
            </PermissionRoute>
          } />
          <Route path="services" element={
            <PermissionRoute resource="admin" action="systemSettings" showAccessDenied>
              <ServicesPage />
            </PermissionRoute>
          } />
          <Route path="tax-rates" element={
            <PermissionRoute resource="admin" action="manageTaxRates" showAccessDenied>
              <TaxRatesPage />
            </PermissionRoute>
          } />
          <Route path="asset-fields" element={<Navigate to="/settings/asset-config" replace />} />
          <Route path="asset-config" element={
            <PermissionRoute resource="admin" action="manageAssetConfig" showAccessDenied>
              <AssetConfigPage />
            </PermissionRoute>
          } />
          <Route path="client-fields" element={
            <PermissionRoute resource="admin" action="manageClientFields" showAccessDenied>
              <ClientFieldsPage />
            </PermissionRoute>
          } />
          <Route path="company" element={
            <PermissionRoute resource="admin" action="manageCompany" showAccessDenied>
              <CompanySettingsPage />
            </PermissionRoute>
          } />
          <Route path="email" element={
            <PermissionRoute resource="admin" action="manageEmail" showAccessDenied>
              <EmailSettingsPage />
            </PermissionRoute>
          } />
          <Route path="system" element={
            <PermissionRoute resource="admin" action="manageSystem" showAccessDenied>
              <SystemSettingsPage />
            </PermissionRoute>
          } />
          <Route path="styling" element={
            <PermissionRoute resource="admin" action="manageStyling" showAccessDenied>
              <AppStylingPage />
            </PermissionRoute>
          } />
          <Route path="ai" element={
            <PermissionRoute resource="admin" action="manageAiSettings" showAccessDenied>
              <AISettingsPage />
            </PermissionRoute>
          } />
          <Route path="changelog" element={<ChangelogPage />} />
          <Route path="completion-rules" element={
            <PermissionRoute resource="admin" action="manageCompletionRules" showAccessDenied>
              <CompletionRulesPage />
            </PermissionRoute>
          } />
          <Route path="service-aliases" element={
            <PermissionRoute resource="admin" action="systemSettings" showAccessDenied>
              <ServiceAliasesPage />
            </PermissionRoute>
          } />
          <Route path="notifications" element={
            <PermissionRoute resource="admin" action="manageNotifications" showAccessDenied>
              <NotificationSettingsPage />
            </PermissionRoute>
          } />
          <Route path="alerts" element={
            <PermissionRoute resource="admin" action="systemSettings" showAccessDenied>
              <AlertSettingsPage />
            </PermissionRoute>
          } />
          <Route path="ninjaone" element={
            <PermissionRoute resource="admin" action="manageNinjaOne" showAccessDenied>
              <NinjaOneSettingsPage />
            </PermissionRoute>
          } />
          <Route path="m365" element={
            <PermissionRoute resource="admin" action="manageM365" showAccessDenied>
              <M365SettingsPage />
            </PermissionRoute>
          } />
          <Route path="xero" element={
            <PermissionRoute resource="admin" action="manageXero" showAccessDenied>
              <XeroSettingsPage />
            </PermissionRoute>
          } />
          <Route path="storage-locations" element={
            <PermissionRoute resource="admin" action="manageStorageLocations" showAccessDenied>
              <StorageLocationsPage />
            </PermissionRoute>
          } />
          <Route path="storage-usage" element={
            <PermissionRoute resource="admin" action="viewStorageUsage" showAccessDenied>
              <StorageUsagePage />
            </PermissionRoute>
          } />
          <Route path="one-off-migrations" element={
            <PermissionRoute resource="admin" action="systemSettings" showAccessDenied>
              <OneOffMigrationsPage />
            </PermissionRoute>
          } />
          <Route path="service-review" element={
            <PermissionRoute resource="admin" action="systemSettings" showAccessDenied>
              <ServiceReviewPage />
            </PermissionRoute>
          } />
          <Route path="internal-categories" element={
            <PermissionRoute resource="admin" action="systemSettings" showAccessDenied>
              <InternalCategoriesPage />
            </PermissionRoute>
          } />
          <Route path="timer-cleanup" element={
            <Navigate to="/settings/health-checks?tab=time-entries" replace />
          } />
          <Route path="health-checks" element={
            <PermissionRoute resource="admin" action="manageHealthChecks" showAccessDenied>
              <HealthChecksPage />
            </PermissionRoute>
          } />
          <Route path="sla" element={
            <PermissionRoute resource="admin" action="manageSla" showAccessDenied>
              <SLASettingsPage />
            </PermissionRoute>
          } />
          <Route path="workflows" element={
            <PermissionRoute resource="admin" action="manageWorkflows" showAccessDenied>
              <WorkflowsPage />
            </PermissionRoute>
          } />
          <Route path="note-templates" element={
            <PermissionRoute roles={['admin', 'manager']} showAccessDenied>
              <NoteTemplatesPage />
            </PermissionRoute>
          } />
          <Route path="workflows/new" element={
            <PermissionRoute resource="admin" action="manageWorkflows" showAccessDenied>
              <SettingsWorkflowBuilderPage />
            </PermissionRoute>
          } />
          <Route path="workflows/:id" element={
            <PermissionRoute resource="admin" action="manageWorkflows" showAccessDenied>
              <SettingsWorkflowBuilderPage />
            </PermissionRoute>
          } />
          <Route path="workflows/:id/log" element={
            <PermissionRoute resource="admin" action="manageWorkflows" showAccessDenied>
              <WorkflowLogPage />
            </PermissionRoute>
          } />
          <Route path="portal" element={
            <PermissionRoute resource="admin" action="managePortal" showAccessDenied>
              <PortalMagicLinksPage />
            </PermissionRoute>
          } />
          <Route path="portal-users" element={
            <PermissionRoute resource="admin" action="managePortal" showAccessDenied>
              <PortalUsersOverviewPage />
            </PermissionRoute>
          } />
          <Route path="byod" element={<Navigate to="/compliance/byod" replace />} />
        </Route>

        {/* Compliance Section */}
        <Route path="compliance">
          <Route index element={<Navigate to="/compliance/byod" replace />} />
          <Route path="byod" element={
            <PermissionRoute resource="compliance" action="view" showAccessDenied>
              <ByodTemplatesPage />
            </PermissionRoute>
          } />
        </Route>
        
        {/* Internal Section */}
        <Route path="internal">
          <Route path="timesheet" element={<InternalTimesheetPage />} />
          <Route path="assets" element={
            <PermissionRoute resource="internal" action="view" showAccessDenied>
              <AssetsPage internal />
            </PermissionRoute>
          } />
          <Route path="assets/new" element={
            <PermissionRoute resource="internal" action="create" showAccessDenied>
              <AssetFormPage />
            </PermissionRoute>
          } />
          <Route path="projects" element={
            <PermissionRoute resource="internal" action="view" showAccessDenied>
              <ProjectsPage internal />
            </PermissionRoute>
          } />
          <Route path="projects/new" element={
            <PermissionRoute resource="projects" action="create" showAccessDenied>
              <ProjectFormPage />
            </PermissionRoute>
          } />
          <Route path="projects/:id" element={
            <PermissionRoute resource="internal" action="view" showAccessDenied>
              <ProjectDetailPage />
            </PermissionRoute>
          } />
          <Route path="tickets/:id" element={
            <PermissionRoute resource="internal" action="view" showAccessDenied>
              <TicketDetailPage />
            </PermissionRoute>
          } />
        </Route>
        
        {/* Profile */}
        <Route path="profile" element={<ProfilePage />} />

        {/* Anything else. Inside the layout so it keeps the sidebar and header, and
            behind ProtectedRoute with the rest, so a logged-out visitor still lands on
            the login page (with the address remembered) instead of a 404. Until
            v1.269.0 an unknown address was silently redirected to the dashboard, which
            is indistinguishable from a link that worked. */}
        <Route path="*" element={<NotFoundPage />} />
      </Route>
      
      {/* Portal routes — completely separate from admin */}
      <Route path="/portal/login" element={
        <PortalAuthProvider>
          <PortalPublicRoute><PortalLoginPage /></PortalPublicRoute>
        </PortalAuthProvider>
      } />
      <Route path="/portal/setup/:token" element={
        <PortalAuthProvider><PortalSetupPage /></PortalAuthProvider>
      } />
      <Route path="/portal/forgot-password" element={
        <PortalAuthProvider><PortalForgotPasswordPage /></PortalAuthProvider>
      } />
      <Route path="/portal/reset-password/:token" element={
        <PortalAuthProvider><PortalResetPasswordPage /></PortalAuthProvider>
      } />
      <Route path="/portal" element={
        <PortalAuthProvider>
          <PortalProtectedRoute><PortalLayout /></PortalProtectedRoute>
        </PortalAuthProvider>
      }>
        <Route index element={<PortalDashboard />} />
        <Route path="tickets" element={<PortalTicketList />} />
        <Route path="tickets/new" element={<PortalTicketNew />} />
        <Route path="tickets/:id" element={<PortalTicketDetail />} />
        <Route path="assets" element={<PortalAssetList />} />
        <Route path="invoices" element={<PortalInvoiceList />} />
        <Route path="invoices/:id" element={<PortalInvoiceDetail />} />
        <Route path="contracts" element={<PortalContractList />} />
        <Route path="contracts/:id" element={<PortalContractDetail />} />
        <Route path="projects" element={<PortalProjectList />} />
        <Route path="projects/:id" element={<PortalProjectDetail />} />
        <Route path="kb" element={<PortalKBList />} />
        <Route path="kb/:id" element={<PortalKBArticle />} />
        <Route path="profile" element={<PortalProfilePage />} />
        <Route path="users" element={<PortalUsersPage />} />
        {/* Unknown portal address — inside the portal layout, so it keeps its chrome. */}
        <Route path="*" element={<PortalNotFoundPage />} />
      </Route>

      {/* Magic link standalone page — no auth required */}
      <Route path="/portal/ticket/:id" element={
        <PortalAuthProvider><PortalMagicTicketPage /></PortalAuthProvider>
      } />

    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <StylingProvider>
            <SettingsValuesProvider>
            <UnsavedChangesProvider>
              <AppRoutes />
              <VersionChecker />
              <Toaster position="bottom-right" richColors closeButton />
            </UnsavedChangesProvider>
            </SettingsValuesProvider>
            </StylingProvider>
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
